import { createSlice } from "@reduxjs/toolkit"

// loopMode cycles: "off" → "song" → "context" → "off"
// "context" loops the current album or playlist depending on what's active

export const slice = createSlice({
  name: "mediaPlayer",
  initialState: {
    value: {
      currentMediaItem:
        JSON.parse(localStorage.getItem("currentMediaItem")) || null,
      playerState: JSON.parse(localStorage.getItem("playerState")) || "idle",
      queue: JSON.parse(localStorage.getItem("mediaPlayerQueue")) || [],
      shuffle: JSON.parse(localStorage.getItem("mediaPlayerShuffle")) || false,
      history: JSON.parse(localStorage.getItem("mediaPlayerHistory")) || [],
      loopMode: JSON.parse(localStorage.getItem("mediaPlayerLoop")) || "off",
    },
  },
  reducers: {
    playNext: (state) => {
      if (state.value.loopMode === "song" && state.value.currentMediaItem) {
        state.value.playerState = "playing"
        localStorage.setItem("playerState", JSON.stringify("playing"))
        return
      }
      playNextHelper(state)
    },

    playPrevious: (state) => {
      if (state.value.history.length === 0) return
      if (state.value.currentMediaItem) {
        state.value.queue = [state.value.currentMediaItem, ...state.value.queue]
        localStorage.setItem("mediaPlayerQueue", JSON.stringify(state.value.queue))
      }
      state.value.currentMediaItem = state.value.history[state.value.history.length - 1]
      state.value.history = state.value.history.slice(0, -1)
      state.value.playerState = "playing"
      localStorage.setItem("currentMediaItem", JSON.stringify(state.value.currentMediaItem))
      localStorage.setItem("playerState", JSON.stringify("playing"))
      localStorage.setItem("mediaPlayerHistory", JSON.stringify(state.value.history))
    },

    pause: (state) => {
      state.value.playerState = "paused"
      localStorage.setItem("playerState", JSON.stringify("paused"))
    },

    playOrPause: (state) => {
      if (state.value.playerState === "playing") {
        state.value.playerState = "paused"
        localStorage.setItem("playerState", JSON.stringify(state.value.playerState))
      } else {
        if (state.value.currentMediaItem !== null) {
          state.value.playerState = "playing"
          localStorage.setItem("playerState", JSON.stringify(state.value.playerState))
        } else {
          playNextHelper(state)
        }
      }
    },

    prependAndPlay: (state, action) => {
      const parsedItems = action.payload.map((item) => mediaItemParser(item))
      state.value.queue = [...parsedItems, ...state.value.queue]
      state.value.history = []
      localStorage.setItem("mediaPlayerHistory", JSON.stringify([]))
      playNextHelper(state)
    },

    pushToQueue: (state, action) => {
      const parsedItems = action.payload.map((item) => mediaItemParser(item))
      const newQueue = [...state.value.queue, ...parsedItems]
      localStorage.setItem("mediaPlayerQueue", JSON.stringify(newQueue))
      state.value.queue = newQueue
    },

    loadTrack: (state, action) => {
      const parsedItems = action.payload.map((item) => mediaItemParser(item))
      state.value.currentMediaItem = parsedItems[0] || null
      state.value.queue = parsedItems.slice(1)
      state.value.history = []
      state.value.playerState = "paused"
      localStorage.setItem("currentMediaItem", JSON.stringify(state.value.currentMediaItem))
      localStorage.setItem("mediaPlayerQueue", JSON.stringify(state.value.queue))
      localStorage.setItem("mediaPlayerHistory", JSON.stringify([]))
      localStorage.setItem("playerState", JSON.stringify("paused"))
    },

    clearQueue: (state) => {
      localStorage.setItem("mediaPlayerQueue", JSON.stringify([]))
      localStorage.setItem("mediaPlayerHistory", JSON.stringify([]))
      state.value.queue = []
      state.value.history = []
    },

    toggleShuffle: (state) => {
      state.value.shuffle = !state.value.shuffle
      localStorage.setItem("mediaPlayerShuffle", JSON.stringify(state.value.shuffle))
    },

    toggleLoop: (state) => {
      const next = { off: "song", song: "context", context: "off" }
      state.value.loopMode = next[state.value.loopMode]
      localStorage.setItem("mediaPlayerLoop", JSON.stringify(state.value.loopMode))
    },

    removeElement: (state, action) => {
      if (action.payload >= 0) {
        const newQueue = state.value.queue
        newQueue.splice(action.payload, 1)
        localStorage.setItem("mediaPlayerQueue", JSON.stringify(newQueue))
        state.value.queue = newQueue
      }
    },
  },
})

const playNextHelper = (state) => {
  if (state.value.currentMediaItem) {
    const newHistory = [...state.value.history, state.value.currentMediaItem].slice(-100)
    state.value.history = newHistory
    localStorage.setItem("mediaPlayerHistory", JSON.stringify(newHistory))
  }

  if (state.value.shuffle && state.value.queue.length > 0) {
    const randomIndex = Math.floor(Math.random() * state.value.queue.length)
    state.value.currentMediaItem = state.value.queue.splice(randomIndex, 1)[0]
  } else {
    state.value.currentMediaItem = state.value.queue.shift() || null
  }

  // Context loop: queue exhausted — reload history as queue and play from top
  if (!state.value.currentMediaItem && state.value.loopMode === "context" && state.value.history.length > 0) {
    state.value.queue = [...state.value.history]
    state.value.history = []
    localStorage.setItem("mediaPlayerHistory", JSON.stringify([]))
    state.value.currentMediaItem = state.value.queue.shift() || null
  }

  state.value.playerState = state.value.currentMediaItem ? "playing" : "idle"
  localStorage.setItem("mediaPlayerQueue", JSON.stringify(state.value.queue))
  localStorage.setItem("currentMediaItem", JSON.stringify(state.value.currentMediaItem))
  localStorage.setItem("playerState", JSON.stringify(state.value.playerState))
}

const mediaItemParser = (mediaItem) => {
  if (
    mediaItem.post_type &&
    ["song", "stand_alone_song"].includes(mediaItem.post_type)
  ) {
    return {
      albumId: mediaItem.album_id,
      albumName: mediaItem.album_name,
      albumPath: mediaItem.album_path,
      bandId: mediaItem.band_id,
      bandName: mediaItem.band_name,
      coverImageSmall:
        mediaItem.cover_image_small || mediaItem.external_photo_src,
      duration: mediaItem.duration,
      localPath: mediaItem.local_path,
      nadaPath: mediaItem.parent_path || mediaItem.nada_path,
      playlistId: mediaItem.playlistId || mediaItem.playlist_id || null,
      playlistName: mediaItem.playlist_name || null,
      playlistPath: mediaItem.playlist_path || null,
      postId: mediaItem.id,
      streamingUrl: mediaItem.streaming_url,
      title: mediaItem.title,
      type: "audio",
    }
  } else {
    return {
      albumName: mediaItem.album_name,
      bandId: mediaItem.band_id,
      bandName: mediaItem.band_name,
      coverImageSmall:
        mediaItem.cover_image_small || mediaItem.external_photo_src,
      duration: mediaItem.duration,
      localPath: mediaItem.local_path,
      nadaPath: mediaItem.parent_path || mediaItem.nada_path,
      playlistId: mediaItem.playlistId || mediaItem.playlist_id || null,
      postId: mediaItem.id,
      albumId: mediaItem.album_id,
      streamingUrl: mediaItem.external_url,
      title: mediaItem.title,
      type: "video",
    }
  }
}

export const {
  pause,
  playNext,
  playPrevious,
  playOrPause,
  prependAndPlay,
  loadTrack,
  pushToQueue,
  removeElement,
  clearQueue,
  toggleShuffle,
  toggleLoop,
} = slice.actions

export const selectMediaPlayer = (state) => state.mediaPlayer.value

export default slice.reducer
